/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useEffect, useRef, Suspense, Fragment } from 'react';
import { Await, useRouteLoaderData } from 'react-router';
import type { loader as rootLoader } from '@/root';
import { shouldRevalidate as shouldRevalidateProduct } from '@/lib/revalidation/routes/product';
import type { Route } from './+types/_app.product.$productId';
import { type ShopperProducts } from '@/scapi';
import { fetchProductById } from '@/lib/api/products.server';
import { fetchCategory } from '@/lib/api/categories.server';
import { fetchWishlistInitialState } from '@/lib/wishlist/fetch-initial-state.server';
import type { WishlistInitialState } from '@/lib/wishlist/state';
import { WishlistProvider } from '@/providers/wishlist';
import { NormalizedApiError } from '@/lib/api/normalized-api-error';
import { siteContext } from '@salesforce/storefront-next-runtime/site-context';
import ProductView from '@/components/product-view';
import ChildProducts from '@/components/product-view/child-products';
import CategoryBreadcrumbs from '@/components/category-breadcrumbs';
import { CategoryBreadcrumbsSkeleton } from '@/components/category-breadcrumbs/skeleton';
import { isProductSet, isProductBundle } from '@/lib/product/product-utils';
import ProductRecommendations from '@/components/product-recommendations';
import { EINSTEIN_RECOMMENDERS } from '@/lib/product/einstein-recommenders';
import { useTranslation } from 'react-i18next';
import { useAnalytics } from '@/hooks/use-analytics';
import { Region } from '@/components/region';
import { ProductProvider } from '@/providers/product-context';
import { PageType } from '@/lib/decorators/page-type';
import { RegionDefinition } from '@/lib/decorators/region-definition';
import { fetchPageWithComponentData } from '@/lib/page-designer/page-loader.server';
import { JsonLd } from '@/components/json-ld';
import { SeoMeta } from '@/components/seo-meta';
import { generateProductSchema } from '@/utils/product-schema';
import { generateProductBreadcrumbSchema } from '@/utils/breadcrumb-schema';
import { getConfig } from '@salesforce/storefront-next-runtime/config';
import { resolveFeatures, resolveModelCode, resolveModelName } from '@/lib/product/custom-attributes';
import { resolveVariantSet } from '@/lib/product/variant-set.server';
import { computeSpecifications } from '@/lib/product/specifications';
import {
    buildVariationsTable,
    type ProductOrderData,
    type VariationsFailureReason,
} from '@/lib/product/variations-table';
import {
    describeEmptySelection,
    selectAttributeLabels,
    selectSpecificationAttributes,
} from '@/lib/product/specification-attributes';
import { fetchProductAttributeGroups } from '@/lib/api/product-attributes.server';
import { getPublicOrigin } from '@/utils/schema-url';
import { buildCanonicalUrl } from '@/utils/canonical-url';
import { getLogger } from '@/lib/logger.server';
import { UITarget } from '@/targets/ui-target';
// @sfdc-extension-block-start SFDC_EXT_BOPIS
import { selectedStoreContext } from '@/extensions/store-locator/middlewares/selected-store.server';
import PickupProvider from '@/extensions/bopis/context/pickup-context';
// @sfdc-extension-block-end SFDC_EXT_BOPIS
// @sfdc-extension-block-start SFDC_EXT_BNPL
import {
    getBuyNowPayLaterMessage,
    getBuyNowPayLaterLearnMore,
    type BuyNowPayLaterMessageData,
    type BuyNowPayLaterLearnMoreData,
} from '@/extensions/bnpl/lib/api/bnpl.server';
import { BnplProvider } from '@/extensions/bnpl/context/bnpl-context';
// @sfdc-extension-block-end SFDC_EXT_BNPL
// @sfdc-extension-block-start SFDC_EXT_RATINGS_REVIEWS
import {
    getReviewsSummary,
    getReviews,
    getWriteReviewForm,
    type ReviewsSummaryData,
    type ReviewsData,
    type WriteReviewFormData,
} from '@/extensions/ratings-reviews/lib/api/reviews.server';
import { ProductReviewsProvider } from '@/extensions/ratings-reviews/providers/product-reviews-context';
import { WriteReviewFormProvider } from '@/extensions/ratings-reviews/context/write-review-form-context';
// @sfdc-extension-block-end SFDC_EXT_RATINGS_REVIEWS
// @sfdc-extension-block-start SFDC_EXT_PRODUCT_CONTENT
import {
    getReturnsAndWarranty,
    getFaqQuestions,
    pdpSectionApi,
    type ReturnsAndWarrantyData,
    type FaqQuestionsData,
    type HtmlContent,
} from '@/extensions/product-content/lib/api/product-content.server';
import { resolvePdpSections } from '@/extensions/product-content/lib/pdp-sections';
import { ProductContentDataProvider } from '@/extensions/product-content/context/product-content-data-context';
// @sfdc-extension-block-end SFDC_EXT_PRODUCT_CONTENT
// @sfdc-extension-block-start SFDC_EXT_SHIPPING_DELIVERY
import {
    getEstimatedDelivery,
    type EstimatedDeliveryData,
} from '@/extensions/shipping-delivery/lib/api/shipping-delivery.server';
import { ShippingDeliveryProvider } from '@/extensions/shipping-delivery/context/shipping-delivery-context';
// @sfdc-extension-block-end SFDC_EXT_SHIPPING_DELIVERY

@PageType({
    name: 'Product Detail Page',
    description: 'Product detail page with product information, images, and recommendations',
    supportedAspectTypes: ['pdp'],
})
@RegionDefinition([
    {
        id: 'promoContent',
        name: 'Promo Content Region',
        description: 'Promotional content region above main product content',
        maxComponents: 1,
    },
    {
        id: 'engagementContent',
        name: 'Engagement Content Region',
        description: 'Engagement content region for recommendations and related products below main content',
        maxComponents: 1,
    },
])
export class ProductPageMetadata {}

export type ProductPageData = {
    product: ShopperProducts.schemas['Product'];
    category: Promise<ShopperProducts.schemas['Category'] | undefined>;
    page: ReturnType<typeof fetchPageWithComponentData>;
    pageKey: string;
    pageUrl: string;
    productSchema: Promise<ReturnType<typeof generateProductSchema> | null>;
    breadcrumbSchema: Promise<ReturnType<typeof generateProductBreadcrumbSchema>>;
    /** Master-level `c_model-code` for the identifier line; `null` when unresolvable. */
    modelCode: Promise<string | null>;
    /**
     * Master-level `c_model-name-for-web` for the `<h1>`; `null` when unresolvable, which the view
     * reads as "keep `product.name`". Resolved, not deferred — see the loader.
     */
    modelName: string | null;
    /** Features copy for the Details panel, variant-first then master; `null` when unresolvable. */
    features: Promise<string | null>;
    /**
     * The Order band's single resolution: the table's model, the catalogue's specification attributes,
     * and the specification union across the whole set. Read by the panel and the table alike, which
     * is why it is one promise rather than two.
     */
    order: Promise<ProductOrderData>;
    wishlistInitialState: Promise<WishlistInitialState>;
    // @sfdc-extension-block-start SFDC_EXT_BNPL
    bnplMessage: Promise<BuyNowPayLaterMessageData>;
    bnplLearnMore: Promise<BuyNowPayLaterLearnMoreData>;
    // @sfdc-extension-block-end SFDC_EXT_BNPL
    // @sfdc-extension-block-start SFDC_EXT_RATINGS_REVIEWS
    reviewsSummary: ReviewsSummaryData;
    reviewsList: Promise<ReviewsData>;
    writeReviewForm: Promise<WriteReviewFormData>;
    // @sfdc-extension-block-end SFDC_EXT_RATINGS_REVIEWS
    // @sfdc-extension-block-start SFDC_EXT_PRODUCT_CONTENT
    returnsWarranty: Promise<ReturnsAndWarrantyData>;
    faqQuestions: Promise<FaqQuestionsData>;
    pdpCollapsibles: Promise<Array<HtmlContent | null>>;
    // @sfdc-extension-block-end SFDC_EXT_PRODUCT_CONTENT
    // @sfdc-extension-block-start SFDC_EXT_SHIPPING_DELIVERY
    estimatedDelivery: Promise<EstimatedDeliveryData>;
    // @sfdc-extension-block-end SFDC_EXT_SHIPPING_DELIVERY
};

/**
 * Server-side loader function that fetches product data and category information.
 * This function runs on the server during SSR and can access cookies for store information.
 *
 * The product is awaited as critical data: a 404 from SCAPI is re-thrown as
 * `Response(message, { status: 404 })` so React Router renders the 404 page with
 * the proper HTTP status (essential for SEO).
 *
 * @returns Object containing the resolved product, deferred category, page data, and schema promises
 */
export async function loader(args: Route.LoaderArgs): Promise<ProductPageData> {
    const { request, params, context } = args;
    const logger = getLogger(context);
    const { productId } = params;
    const requestUrl = new URL(request.url);
    const { searchParams } = requestUrl;
    const variantPid = searchParams.get('pid');
    logger.debug('Product: loader starting', { productId, variantPid: variantPid || undefined });

    // @sfdc-extension-block-start SFDC_EXT_BOPIS
    const selectedStoreInfo = context.get(selectedStoreContext);
    // @sfdc-extension-block-end SFDC_EXT_BOPIS

    // Get currency from context for product pricing
    const siteCtx = context.get(siteContext);
    if (!siteCtx) {
        logger.error('Product: site context is not available');
        throw new Response('Site context is not available', { status: 500 });
    }
    const { currency } = siteCtx;

    // Resolve the product critically. A 404 here must propagate as Response(404)
    // so the route error boundary renders the 404 page with the correct HTTP status.
    const productLookupId = variantPid || productId;

    // @sfdc-extension-block-start SFDC_EXT_RATINGS_REVIEWS
    // Start reviews summary fetch in parallel with the product fetch — it only
    // needs the product ID and drives above-the-fold star display + SEO.
    const reviewsSummaryPromise = getReviewsSummary(productLookupId);
    // @sfdc-extension-block-end SFDC_EXT_RATINGS_REVIEWS

    let product: ShopperProducts.schemas['Product'] | null;
    try {
        product = await fetchProductById(context, productLookupId, {
            expand: [
                'availability', // <-- TTL = 60s (!)
                'bundled_products',
                'images',
                'options',
                'page_meta_tags',
                'prices', // <-- TTL = 900s
                'promotions', // <-- TTL = 900s
                'set_products',
                'variations',
            ],
            allImages: true,
            perPricebook: true,
            ...(currency ? { currency } : {}),
            // @sfdc-extension-block-start SFDC_EXT_BOPIS
            // Include inventoryIds parameter when store is selected
            ...(selectedStoreInfo?.inventoryId ? { inventoryIds: [selectedStoreInfo.inventoryId] } : {}),
            // @sfdc-extension-block-end SFDC_EXT_BOPIS
        });
    } catch (e) {
        if (e instanceof NormalizedApiError && e.status) {
            throw new Response(e.message, { status: e.status });
        }
        throw new Response('Internal Server Error', { status: 500 });
    }

    if (!product) {
        throw new Response('Product not found', { status: 404 });
    }

    const masterProductPromise = (() => {
        if (product.master?.masterId) {
            return fetchProductById(context, product.master.masterId, {
                ...(currency ? { currency } : {}),
            });
        }

        return null;
    })();

    // The product's category, resolved ONCE and shared by the two consumers that need it — the
    // breadcrumb trail and the Page Designer lookup. Each computed this same expression for itself
    // before the hoist.
    //
    // NOT an input to the specifications panel, which keys off the CLASSIFICATION category instead.
    // The two are different id spaces and are not interchangeable: a rear shock classifies as
    // `suspension-rear` while its primary category is `mountain-rear-shocks`, and the site catalogue
    // splits by ride type so one classification spans three primaries. Only the attribute-model
    // endpoint knows the former.
    //
    // A variant carries no `primaryCategoryId` of its own and inherits its master's, which is why the
    // master branch exists; it costs no request, since `masterProductPromise` is already in flight,
    // and no wait on a master or standalone URL, which answers from its own payload.
    //
    // NOT error-handled here, deliberately: both consumers propagate a master failure today —
    // breadcrumbs degrade through `<Await errorElement={null}>` — and hoisting is not the place to
    // change that.
    const primaryCategoryIdPromise: Promise<string | undefined> = product.primaryCategoryId
        ? Promise.resolve(product.primaryCategoryId)
        : Promise.resolve(masterProductPromise).then((masterProduct) => masterProduct?.primaryCategoryId);

    // Identifier line under the product name. Always-master (policy 1); both resolvers reuse
    // `masterProductPromise`, already in flight for the category lookup, so neither costs an extra
    // SCAPI call. NOT awaited — an identifier drives no SEO, LCP, CLS or HTTP status.
    const modelCodePromise = resolveModelCode(product, masterProductPromise, (error) => {
        logger.error('Error resolving master model code in loader', { error });
    });

    // The `<h1>`. Started here but AWAITED below, unlike `modelCode` above, despite sharing a policy
    // and a master fetch: a heading is the page's primary on-page SEO signal and an LCP candidate,
    // and a 40px heading has no honest skeleton width, so deferring it would trade a blocking wait
    // for CLS. The added latency lands only on variant URLs — a master or standalone resolves from
    // its own payload with no wait, and the canonical PDP URL is the master's.
    const modelNamePromise = resolveModelName(product, masterProductPromise);

    // Specifications panel. Two independent reads, joined: WHICH attributes the product's category
    // displays (the catalogue's attribute model) and WHAT VALUES its variants hold.
    //
    // NOT a per-product attribute read: the rows are a union computed across every variant of the
    // master, so a variant URL and its master URL produce the same panel.
    //
    // Composed HERE, never in render — Suspense tracks promises by identity, and an in-render
    // composition mints a new reference every render and re-suspends forever. That applies to the
    // `Promise.all` too: it is the reason both inputs are joined in the loader rather than passing
    // two promises to the component and combining them there.
    //
    // Costs two requests, both non-blocking: one batched `getProducts` (`resolveVariantSet` reuses
    // `masterProductPromise` rather than fetching its own master) and one attribute-model lookup,
    // itself cached per category per locale on the server. Not awaited — a specifications table drives
    // no SEO, LCP, CLS or HTTP status.
    //
    // An empty result renders NO PANEL, deliberately, and is logged with which of the two empty cases
    // it was. There is no static fallback list: a storefront cannot know a category's specifications,
    // and a plausible-looking default is how a PIM gap stays invisible.
    // ONE resolution for the whole page. The specifications panel collapses this set into a union; the
    // variations table keeps one row per member. Two consumers, one batched `getProducts` — resolving
    // it twice would double the request for identical data.
    let truncation: { total: number; used: number } | undefined;

    /**
     * Set when the variant set cannot back a purchase control, so the table renders an error instead of
     * rows. Captured the same way `truncation` is, and safe for the same reason: every path that sets it
     * runs before the set's promise resolves, so it is present by the time the join below reads it.
     */
    let failure: { reason: VariationsFailureReason } | undefined;

    /** The model this page belongs to, for a log line that identifies the data to fix. */
    const masterIdForLogs = product.master?.masterId ?? product.id;

    const variantSetPromise = resolveVariantSet(
        context,
        product,
        masterProductPromise,
        (error) => {
            // Transport, not data. The cause is what distinguishes a timeout from a 404 from a bad
            // credential, and none of them are diagnosable from "the table was empty".
            logger.error('Error resolving variant set in loader', { error, masterId: masterIdForLogs });

            failure = { reason: 'error' };
        },
        ({ productId: masterId, total, used }) => {
            // A partial set is a WRONG panel and a wrong order table, not a slow one — worth a log
            // line rather than silence.
            logger.warn('Variant set truncated', { productId: masterId, total, used });

            // Captured, not only logged: the table has to SAY it is partial. The callback runs before
            // the set's promise resolves, so this is set by the time the join below reads it.
            truncation = { total, used };
        },
        // The same currency the page's own product was fetched with, so a row's price and the buy
        // box's cannot be quoted in two different ones on one screen.
        { currency },
        (unresolved) => {
            // Two data problems, two audiences, one outcome on screen. The reader can only reload
            // either way; the log is what tells the difference to whoever can actually fix it.
            if (unresolved.reason === 'master-has-no-variants') {
                // WARN, not error: nothing failed. A master was published with no variants, which is a
                // catalogue-authoring gap the PIM team resolves by declaring them.
                logger.warn('Master declares no variants; order table will not render', {
                    masterId: unresolved.masterId,
                    variantCount: 0,
                });
            } else {
                // ERROR: the master names variants the catalogue does not return, so two systems
                // disagree about what exists. The ids are the whole value of the line.
                logger.error('Declared variants did not resolve; order table will not render', {
                    masterId: unresolved.masterId,
                    variantIds: unresolved.variantIds,
                });
            }

            failure = { reason: unresolved.reason };
        }
    );

    // ONE resolution, TWO consumers with their own boundaries. The panel collapses the set into a
    // union; the table keeps one row per member; and once filters exist the panel must recompute over
    // whatever they leave visible, which needs the same set and the same attribute list. Composed here
    // rather than in render, because Suspense tracks promises by identity and an in-render join mints a
    // new reference every render.
    //
    // The attribute list is carried to the client, not just the rows it produced. The rows below are
    // the unfiltered answer, correct on first paint and for every reader who never touches a filter;
    // the attributes are what let the panel recompute when one is set, with no further request.
    //
    // Not awaited: neither a specifications panel nor an order table drives SEO, LCP, CLS or HTTP status.
    const orderPromise: Promise<ProductOrderData> = Promise.all([
        variantSetPromise,
        Promise.resolve(masterProductPromise),
        // `.catch` is belt-and-braces: the client is contracted never to throw and logs its own
        // failures. It is here so a future edit that breaks that contract degrades the panel instead
        // of rejecting a loader promise the component renders through `use()`.
        fetchProductAttributeGroups(context, productLookupId).catch(() => null),
    ])
        .then(([variantSet, masterProduct, attributeGroups]) => {
            // The catalogue's declared variation attributes are read from the MASTER, because a variant
            // carries none of its own. They are a PREFERENCE for which column heads the table, never
            // the source of the column set: PIM declares them inconsistently, and a table built from
            // them shows no distinguishing columns on a model whose variants plainly differ.
            const declared = (masterProduct ?? product).variationAttributes ?? [];
            const declaredIds = declared
                .map((attribute) => attribute.id)
                .filter((id): id is string => typeof id === 'string');

            // The panel drops the variation dimensions: their union across the set is the variant list,
            // and the table's own columns and filters already present it.
            const specificationAttributes = selectSpecificationAttributes(attributeGroups, declaredIds);

            // Column headings come from Business Manager, exactly as the panel's row labels do — and
            // from the DEFINITIONS, not from the panel's selection. Built from the selection, an
            // attribute the panel excludes reached the table with no name and fell back to humanising
            // its id, which is how `Eye to Eye` once became `Eye To Eye Stroke`.
            //
            // `specification-labels.ts` is emphatic that humanisation is a SAFETY NET and that the
            // frontend must not become a second source of truth for merchant wording. A hardcoded
            // `Eye to Eye` here would be exactly the override that module deleted on purpose: if
            // Business Manager still says `Eye to Eye Stroke`, the fix is one field there, and it lands
            // on every channel at once.
            const model = buildVariationsTable(variantSet, declaredIds, selectAttributeLabels(attributeGroups));

            // An empty result renders NO PANEL, deliberately, and is logged with which of the two empty
            // cases it was. There is no static fallback list: a storefront cannot know a category's
            // specifications, and a plausible-looking default is how a PIM gap stays invisible.
            if (!specificationAttributes.length) {
                logger.warn('No specification attributes resolved; panel will not render', {
                    productId: productLookupId,
                    reason: describeEmptySelection(attributeGroups, declaredIds),
                });

                return {
                    model,
                    specificationAttributes: [],
                    specificationRows: [],
                    ...(truncation ? { truncation } : {}),
                    ...(failure ? { failure } : {}),
                };
            }

            return {
                model,
                specificationAttributes,
                specificationRows: computeSpecifications(variantSet, specificationAttributes),
                ...(truncation ? { truncation } : {}),
                ...(failure ? { failure } : {}),
            };
        })
        // Both consumers render this through `use()` with no error boundary between them, so a throw in
        // selection or projection would take the page down rather than the section. Every input already
        // degrades on its own; this covers the join.
        .catch((error) => {
            logger.error('Error resolving order data in loader', { error, masterId: masterIdForLogs });

            // A throw here leaves the table with no rows AND no explanation, which is the state the
            // error notice exists for. Reported as `error` rather than left empty: a silent empty band
            // is what made a broken page indistinguishable from a model nobody stocked.
            return {
                model: { secondary: [], filters: [], rows: [] },
                specificationAttributes: [],
                specificationRows: [],
                failure: { reason: 'error' as const },
            };
        });

    // Build the deferred category promise. Category is optional context for the
    // breadcrumbs — failures degrade silently via the route-level <Await errorElement={null}>.
    const categoryPromise: Promise<ShopperProducts.schemas['Category'] | undefined> = primaryCategoryIdPromise.then(
        (primaryCategoryId) => (primaryCategoryId ? fetchCategory(context, primaryCategoryId, 1) : undefined)
    );

    const pageUrl = buildCanonicalUrl(requestUrl.origin, requestUrl.pathname, requestUrl.search);

    // Shared by both JSON-LD promises below. Deliberately NOT reusing `pageUrl` above: that is
    // the canonical URL and builds on `requestUrl.origin`, whereas schema has to use the public
    // origin from the forwarding headers so serverless deployments never publish internal AWS
    // Lambda URLs in structured data. Same page, two different origins — keep both names.
    // Computing it eagerly, outside the promises' error handling, adds no failure path:
    // `getPublicOrigin` catches its own errors and always returns a string.
    const schemaPageUrl = `${getPublicOrigin(request)}${requestUrl.pathname}${requestUrl.search}`;

    // Generate product schema in loader (server-side) for SEO.
    // Wrapped in a Promise so it can be rendered through Suspense without blocking
    // the loader response. The inner try/catch logs synchronous schema-generation failures
    // (this is local computation, not a SCAPI call — `fetchProductById` already logs SCAPI
    // errors at the API layer) so we keep visibility on rare malformed-input bugs.
    // Render-time failures degrade silently via the route-level <Await errorElement={null}>.
    const productSchemaPromise: Promise<ReturnType<typeof generateProductSchema> | null> = Promise.resolve().then(
        () => {
            try {
                return generateProductSchema(product, schemaPageUrl);
            } catch (error) {
                logger.error('Error generating product schema in loader', { error });
                return null;
            }
        }
    );

    // Breadcrumb structured data, derived from the category already being fetched for the
    // visible trail — no extra SCAPI call. Composed HERE in the loader, never in the
    // component: an in-render composition produces a new promise each render and re-suspends
    // forever. Resolves to null when there is no category, so nothing is emitted.
    const breadcrumbSchemaPromise: Promise<ReturnType<typeof generateProductBreadcrumbSchema>> = categoryPromise
        .then((category) => {
            if (!category) return null;

            return generateProductBreadcrumbSchema({
                category,
                pageUrl: schemaPageUrl,
                rootCategoryId: getConfig(context).pages.navigation.rootCategoryId,
            });
        })
        .catch((error) => {
            logger.error('Error generating breadcrumb schema in loader', { error });
            return null;
        });

    const pagePromise = (async () => {
        const primaryCategoryId = await primaryCategoryIdPromise;

        return fetchPageWithComponentData(args, {
            aspectType: 'pdp',
            productId: productLookupId,
            ...(primaryCategoryId ? { categoryId: primaryCategoryId } : {}),
        });
    })();

    // Awaited last, after every deferred fetch above is already in flight, so the heading's wait
    // overlaps them instead of delaying them.
    const modelName = await modelNamePromise;

    // @sfdc-extension-block-start SFDC_EXT_RATINGS_REVIEWS
    // Await the summary started earlier (ran in parallel with fetchProductById).
    const reviewsSummary = await reviewsSummaryPromise;
    const reviewsList = getReviews(productLookupId);
    const writeReviewForm = getWriteReviewForm(productLookupId);
    // @sfdc-extension-block-end SFDC_EXT_RATINGS_REVIEWS

    // KNOWN DEBT (CRCB2B-313): six of the fetches below feed blocks now switched off in their
    // `target-config.json`. The gate removes the component from the render registry, not the fetch.
    // Harmless while they are demo stubs returning `Promise.resolve(MOCK_*)` — but `reviewsSummary`
    // is `await`ed above, so whoever wires the first real provider must delete its loader field and
    // plumbing at the same time or the PDP blocks the document on data nothing renders.
    return {
        product,
        category: categoryPromise,
        /**
         * Fetch page data from Page Designer API with nested componentData promises.
         * Handle errors gracefully - return page with empty componentData if fetch failed.
         */
        page: pagePromise,
        pageKey: productId,
        pageUrl,
        productSchema: productSchemaPromise,
        breadcrumbSchema: breadcrumbSchemaPromise,
        modelCode: modelCodePromise,
        modelName,
        // Variant-first (policy 2) — the OPPOSITE rule to `modelCode` above. Not awaited: body copy
        // drives no SEO, LCP, CLS or HTTP status, and only a variant falling back to its master waits
        // at all.
        features: resolveFeatures(product, masterProductPromise),
        order: orderPromise,
        wishlistInitialState: fetchWishlistInitialState(context),
        // @sfdc-extension-block-start SFDC_EXT_BNPL
        bnplMessage: getBuyNowPayLaterMessage(productLookupId),
        bnplLearnMore: getBuyNowPayLaterLearnMore(productLookupId),
        // @sfdc-extension-block-end SFDC_EXT_BNPL
        // @sfdc-extension-block-start SFDC_EXT_RATINGS_REVIEWS
        reviewsSummary,
        reviewsList,
        writeReviewForm,
        // @sfdc-extension-block-end SFDC_EXT_RATINGS_REVIEWS
        // @sfdc-extension-block-start SFDC_EXT_PRODUCT_CONTENT
        returnsWarranty: getReturnsAndWarranty(productLookupId),
        faqQuestions: getFaqQuestions(productLookupId),
        pdpCollapsibles: Promise.all(
            resolvePdpSections(product).map((section) =>
                pdpSectionApi[section.apiMethod](productLookupId).catch(() => null)
            )
        ),
        // @sfdc-extension-block-end SFDC_EXT_PRODUCT_CONTENT
        // @sfdc-extension-block-start SFDC_EXT_SHIPPING_DELIVERY
        estimatedDelivery: getEstimatedDelivery(productLookupId),
        // @sfdc-extension-block-end SFDC_EXT_SHIPPING_DELIVERY
    };
}

// Gates both the navigation axis (skip client-only param changes like color/size) and the action axis (skip the
// expensive loader re-run after cart/wishlist/account mutations that touch nothing the loader reads). The policy and
// its denylist live in the shared module so the rationale is documented and unit-tested in one place.
export const shouldRevalidate = shouldRevalidateProduct;

function ProductContent({
    product,
    url,
    modelCodePromise,
    modelName,
    featuresPromise,
    orderPromise,
    // @sfdc-extension-block-start SFDC_EXT_RATINGS_REVIEWS
    reviewsSummary,
    reviewsList,
    writeReviewForm,
    // @sfdc-extension-block-end SFDC_EXT_RATINGS_REVIEWS
    // @sfdc-extension-block-start SFDC_EXT_PRODUCT_CONTENT
    returnsWarrantyPromise,
    faqQuestionsPromise,
    pdpCollapsiblesPromise,
    // @sfdc-extension-block-end SFDC_EXT_PRODUCT_CONTENT
}: {
    product: ShopperProducts.schemas['Product'];
    url: string;
    modelCodePromise: Promise<string | null>;
    modelName: string | null;
    featuresPromise: Promise<string | null>;
    orderPromise: Promise<ProductOrderData>;
    // @sfdc-extension-block-start SFDC_EXT_RATINGS_REVIEWS
    reviewsSummary: ReviewsSummaryData;
    reviewsList: Promise<ReviewsData>;
    writeReviewForm: Promise<WriteReviewFormData>;
    // @sfdc-extension-block-end SFDC_EXT_RATINGS_REVIEWS
    // @sfdc-extension-block-start SFDC_EXT_PRODUCT_CONTENT
    returnsWarrantyPromise: Promise<ReturnsAndWarrantyData>;
    faqQuestionsPromise: Promise<FaqQuestionsData>;
    pdpCollapsiblesPromise: Promise<Array<HtmlContent | null>>;
    // @sfdc-extension-block-end SFDC_EXT_PRODUCT_CONTENT
}) {
    const analytics = useAnalytics();
    const lastTrackedProductIdRef = useRef<string | null>(null);

    const primaryImage =
        product.imageGroups?.find((g) => g.viewType === 'large')?.images?.[0]?.link ??
        product.imageGroups?.[0]?.images?.[0]?.link;

    // Track product view on mount and whenever productData changes
    useEffect(() => {
        // Only track if we haven't already tracked this product
        if (product.id !== lastTrackedProductIdRef.current) {
            void analytics.trackViewProduct({
                product,
            });
            lastTrackedProductIdRef.current = product.id;
        }
    }, [analytics, product]);

    const isProductASet = isProductSet(product);
    const isProductABundle = isProductBundle(product);

    return (
        <ProductProvider product={product}>
            {/* @sfdc-extension-block-start SFDC_EXT_PRODUCT_CONTENT */}
            <ProductContentDataProvider
                product={product}
                returnsWarrantyPromise={returnsWarrantyPromise}
                faqQuestionsPromise={faqQuestionsPromise}
                pdpCollapsiblesPromise={pdpCollapsiblesPromise}>
                {/* @sfdc-extension-block-end SFDC_EXT_PRODUCT_CONTENT */}
                {/* @sfdc-extension-block-start SFDC_EXT_RATINGS_REVIEWS */}
                {/* Provider wraps ProductView so the in-page rating summary shares state
                        with the customer reviews accordion (expand/jump-to coordination). */}
                <ProductReviewsProvider summary={reviewsSummary} reviewsListPromise={reviewsList}>
                    <WriteReviewFormProvider writeReviewFormPromise={writeReviewForm}>
                        {/* @sfdc-extension-block-end SFDC_EXT_RATINGS_REVIEWS */}
                        <SeoMeta
                            title={product.name}
                            description={product.pageDescription || product.shortDescription}
                            openGraph={{
                                type: 'product',
                                url,
                                image: primaryImage,
                            }}
                        />
                        <div className="space-y-8">
                            {isProductASet || isProductABundle ? (
                                <>
                                    <ProductView
                                        product={product}
                                        modelCodePromise={modelCodePromise}
                                        modelName={modelName}
                                        featuresPromise={featuresPromise}
                                        orderPromise={orderPromise}
                                    />
                                    <ChildProducts parentProduct={product} />
                                </>
                            ) : (
                                <ProductView
                                    product={product}
                                    modelCodePromise={modelCodePromise}
                                    modelName={modelName}
                                    featuresPromise={featuresPromise}
                                    orderPromise={orderPromise}
                                />
                            )}

                            {/* @sfdc-extension-block-start SFDC_EXT_RATINGS_REVIEWS */}
                            {/* Kept while switched off: `"enabled": false` on this entry in
                                ratings-reviews/target-config.json, so re-enabling is a one-word JSON edit.
                                Only THIS entry is disabled — `sfcc.pdp.reviews.rating` still serves the
                                cart-item modal (the PDP header suppresses it separately via `hideRating`) and
                                `sfcc.account.orderDetail.lineReview` still serves order detail. */}
                            <UITarget targetId="sfcc.pdp.reviews.section" />
                            {/* @sfdc-extension-block-end SFDC_EXT_RATINGS_REVIEWS */}
                            <UITarget targetId="sfcc.pdp.reviews.qna" />
                        </div>
                        {/* @sfdc-extension-block-start SFDC_EXT_RATINGS_REVIEWS */}
                    </WriteReviewFormProvider>
                </ProductReviewsProvider>
                {/* @sfdc-extension-block-end SFDC_EXT_RATINGS_REVIEWS */}
                {/* @sfdc-extension-block-start SFDC_EXT_PRODUCT_CONTENT */}
            </ProductContentDataProvider>
            {/* @sfdc-extension-block-end SFDC_EXT_PRODUCT_CONTENT */}
        </ProductProvider>
    );
}

/**
 * Product detail shell that composes the page layout with granular Suspense boundaries.
 * Regions render independently (they manage their own async via Suspense/Await),
 * while the core product content renders synchronously from the resolved loader data.
 */
function ProductDetailView({ loaderData }: { loaderData: ProductPageData }) {
    const { t } = useTranslation('product');
    const content = (
        <div className="min-h-screen bg-background">
            <div className="section-container pb-4 lg:pb-8">
                {/* Promo Content Region - Promotional content above main product */}
                <Region className="mb-8" page={loaderData.page} regionId="promoContent" />

                {/* Paired with ProductView's own gap-4 — breadcrumbs, header and tabs live in separate
                    components but read as one 16px column. Gap, not margin, so nothing collapses. */}
                <div className="flex flex-col gap-4">
                    {/* Category breadcrumbs - streams independently of product data.
                        Breadcrumbs are non-critical: errorElement renders nothing so a category
                        fetch failure silently degrades to an empty breadcrumbs row.
                        `leafIsCurrentPage={false}`: the trail ends on the product's category, but
                        the page being viewed is the product — which is not an entry — so the final
                        crumb stays navigable and claims no `aria-current`.

                        The skeleton reserves exactly the 36px the resolved trail occupies, so the
                        normal path — category arrives, trail replaces skeleton — shifts nothing.

                        OPEN: the `category ? ... : null` branch below does still shift. A product
                        with no `primaryCategoryId` and no master to inherit one from resolves to
                        `undefined`, and the row collapses 36px -> 0px. Sizing the skeleton
                        correctly makes that jump larger, not smaller — the two are different
                        problems. Fixing it means deciding whether the breadcrumb row should hold
                        its height unconditionally, reserving space on every PDP for a trail that
                        may never arrive; that is a design call, not a bug fix, and is deliberately
                        left open here. */}
                    <Suspense fallback={<CategoryBreadcrumbsSkeleton />}>
                        <Await resolve={loaderData.category} errorElement={null}>
                            {(category) =>
                                category ? <CategoryBreadcrumbs category={category} leafIsCurrentPage={false} /> : null
                            }
                        </Await>
                    </Suspense>

                    {/* Main Product Content — product is resolved synchronously by the loader */}
                    <ProductContent
                        product={loaderData.product}
                        url={loaderData.pageUrl}
                        modelCodePromise={loaderData.modelCode}
                        modelName={loaderData.modelName}
                        featuresPromise={loaderData.features}
                        orderPromise={loaderData.order}
                        // @sfdc-extension-block-start SFDC_EXT_RATINGS_REVIEWS
                        reviewsSummary={loaderData.reviewsSummary}
                        reviewsList={loaderData.reviewsList}
                        writeReviewForm={loaderData.writeReviewForm}
                        // @sfdc-extension-block-end SFDC_EXT_RATINGS_REVIEWS
                        // @sfdc-extension-block-start SFDC_EXT_PRODUCT_CONTENT
                        returnsWarrantyPromise={loaderData.returnsWarranty}
                        faqQuestionsPromise={loaderData.faqQuestions}
                        pdpCollapsiblesPromise={loaderData.pdpCollapsibles}
                        // @sfdc-extension-block-end SFDC_EXT_PRODUCT_CONTENT
                    />
                </div>

                {/* Engagement Content Region - Shows page content or recommendations */}
                <Region
                    className="mt-16"
                    page={loaderData.page}
                    regionId="engagementContent"
                    errorElement={
                        <div className="mt-16 space-y-16">
                            <ProductRecommendations
                                recommenderName={EINSTEIN_RECOMMENDERS.PDP_COMPLETE_SET}
                                recommenderTitle={t('recommendations.completeTheLook')}
                                className="max-w-none px-0"
                            />
                            <ProductRecommendations
                                recommenderName={EINSTEIN_RECOMMENDERS.PDP_MIGHT_ALSO_LIKE}
                                recommenderTitle={t('recommendations.youMightAlsoLike')}
                                className="max-w-none px-0"
                            />
                            <ProductRecommendations
                                recommenderName={EINSTEIN_RECOMMENDERS.PDP_RECENTLY_VIEWED}
                                recommenderTitle={t('recommendations.recentlyViewed')}
                                className="max-w-none px-0"
                            />
                        </div>
                    }
                />
            </div>
        </div>
    );

    let finalContent = content;
    // @sfdc-extension-block-start SFDC_EXT_BOPIS
    finalContent = <PickupProvider>{finalContent}</PickupProvider>;
    // @sfdc-extension-block-end SFDC_EXT_BOPIS
    // @sfdc-extension-block-start SFDC_EXT_BNPL
    finalContent = (
        <BnplProvider messagePromise={loaderData.bnplMessage} learnMorePromise={loaderData.bnplLearnMore}>
            {finalContent}
        </BnplProvider>
    );
    // @sfdc-extension-block-end SFDC_EXT_BNPL
    // @sfdc-extension-block-start SFDC_EXT_SHIPPING_DELIVERY
    finalContent = (
        <ShippingDeliveryProvider estimatedDeliveryPromise={loaderData.estimatedDelivery}>
            {finalContent}
        </ShippingDeliveryProvider>
    );
    // @sfdc-extension-block-end SFDC_EXT_SHIPPING_DELIVERY

    return finalContent;
}

/**
 * Product page component that displays a product with its details and category breadcrumbs.
 * The page key ensures React only remounts when navigating to a different product, not variants.
 * @returns JSX element representing the product page with Suspense boundary
 */
export default function ProductPage({ loaderData }: { loaderData: ProductPageData }) {
    // Use pageKey from loaderData to force remount only when productId changes
    // This prevents showing skeleton when switching variants (pid parameter)
    const pageKey = loaderData.pageKey;

    const rootData = useRouteLoaderData<typeof rootLoader>('root');
    const nonce = rootData?.nonce ?? undefined;

    return (
        <WishlistProvider initialState={loaderData.wishlistInitialState}>
            <Fragment key={pageKey}>
                <ProductDetailView loaderData={loaderData} />

                {/* Product JSON-LD Schema for SEO - render after page content so it appears at end of body flow.
                    JSON-LD is non-critical: errorElement renders nothing so a schema-generation failure
                    silently degrades to no <script> tag. */}
                <Suspense fallback={null}>
                    <Await resolve={loaderData.productSchema} errorElement={null}>
                        {(productSchema) =>
                            productSchema ? <JsonLd data={productSchema} id="product-schema" nonce={nonce} /> : null
                        }
                    </Await>
                </Suspense>

                {/* Breadcrumb JSON-LD — its own boundary because it resolves independently of
                    the product schema. Invisible, so fallback={null} reserves nothing and
                    causes no layout shift. */}
                <Suspense fallback={null}>
                    <Await resolve={loaderData.breadcrumbSchema} errorElement={null}>
                        {(breadcrumbSchema) =>
                            breadcrumbSchema ? (
                                <JsonLd data={breadcrumbSchema} id="breadcrumb-schema" nonce={nonce} />
                            ) : null
                        }
                    </Await>
                </Suspense>
            </Fragment>
        </WishlistProvider>
    );
}
